import { useCallback, useMemo } from 'react';
import { BenchmarkGrid } from '@strength/domain';
import { trpc } from '../../trpc/react';
import { RouterOutput } from '../../trpc';

/** A benchmark grid plus the `teamId` it belongs to (grids resolve per-team on the tablet). */
export type TabletBenchmarkGrid = BenchmarkGrid & { teamId: string };

/** A multi-team tablet benchmark endpoint row: a `BenchmarkGrid` row + its `coreExercises`. */
type BenchmarkGridRow = RouterOutput['tablet']['benchmarkGrids'][number];

function toTabletBenchmarkGrid(row: BenchmarkGridRow): TabletBenchmarkGrid {
  return {
    id: row.id,
    name: row.name,
    type: row.type,
    repCount: row.repCount,
    updateType: row.updateType,
    isDefault: row.isDefault,
    coreExercises: row.coreExercises,
    teamId: row.teamId,
  };
}

/** Resolves a grid by id across all teams' grids; `null` if not loaded or not found. */
export function resolveBenchmarkGrid(
  grids: readonly TabletBenchmarkGrid[],
  id: string,
): BenchmarkGrid | null {
  if (!grids.length) return null;
  return grids.find((g) => g.id === id) ?? null;
}

/** Resolves a team's default grid id from the multi-team result; `''` if the team has none. */
export function resolveDefaultBenchmarkGridId(
  defaultGrids: readonly TabletBenchmarkGrid[],
  teamId: string,
): string {
  return defaultGrids.find((g) => g.teamId === teamId)?.id ?? '';
}

/**
 * Multi-team variant of `useBenchmarks` for the tablet collection screen: fetches
 * grids for all selected teams in one shared request and resolves them per team.
 * Empty `teamIds` → no request fires and resolvers behave as "not loaded".
 */
export function useTabletBenchmarks(teamIds: string[]) {
  // The endpoints are header-only (scope comes from `tw-team-ids`, not input),
  // so we only gate on having a scope. `teamIds` is still used to resolve grids
  // per athlete's team client-side below. Freshness on scope change is handled
  // by queryKeyHashFn: TabletActualsPage sets the header synchronously during
  // render, so a new scope produces a new hash → cache miss → fresh fetch.
  const enabled = teamIds.length > 0;

  const query = trpc.tablet.benchmarkGrids.useQuery(undefined, {
    staleTime: Infinity,
    enabled,
  });
  const defaultQuery = trpc.tablet.defaultBenchmarkGrids.useQuery(undefined, {
    staleTime: Infinity,
    enabled,
  });

  const { benchmarkGrids, defaultBenchmarkGrids } = useMemo(() => {
    const benchmarkGrids = query.data?.map(toTabletBenchmarkGrid) ?? [];
    const defaultBenchmarkGrids =
      defaultQuery.data?.map(toTabletBenchmarkGrid) ?? [];
    return { benchmarkGrids, defaultBenchmarkGrids };
  }, [query.data, defaultQuery.data]);

  const getBenchmarkGrid = useCallback(
    (id: string): BenchmarkGrid | null =>
      resolveBenchmarkGrid(benchmarkGrids, id),
    [benchmarkGrids],
  );

  const getDefaultBenchmarkGridId = useCallback(
    (teamId: string): string =>
      resolveDefaultBenchmarkGridId(defaultBenchmarkGrids, teamId),
    [defaultBenchmarkGrids],
  );

  const { refetch } = query;
  const { refetch: refetchDefaults } = defaultQuery;
  const rehydrate = useCallback(() => {
    refetch();
    refetchDefaults();
  }, [refetch, refetchDefaults]);

  return {
    benchmarkGrids,
    getBenchmarkGrid,
    getDefaultBenchmarkGridId,
    isLoading: query.isLoading,
    error: query.error?.message,
    rehydrate,
  };
}
